import styled, { css } from "styled-components";
import {
    display,
    type DisplayProps,
    flexbox,
    type FlexboxProps,
    layout,
    type LayoutProps,
    space,
    type SpaceProps,
    width,
    type WidthProps,
} from "styled-system";
import { Button, ButtonWrapper } from "../globals/Button";
import * as Text from "../utilities/Text";
import { type ColorType, theme as lexusTheme } from "../theme/lexusTheme";
import { IconWrapper } from "../globals/IconWrapper";
import { Ellipsis } from "../utilities/Ellipsis";

const BUTTON_SIZE_TYPES = ["sm", "lg", "xl"];

type ButtonType = {
    large?: boolean;
    light?: boolean;
    hidden?: boolean;
    selected?: boolean;
    hasIconLeft?: boolean;
    hasIconRight?: boolean;
    fullWidth?: boolean;
    buttonSize?: (typeof BUTTON_SIZE_TYPES)[number];
} & DisplayProps &
    FlexboxProps &
    LayoutProps &
    WidthProps &
    SpaceProps;

export const styleProps = css`
    ${display};
    ${flexbox};
    ${layout};
    ${space};
    ${width};
`;

/**
 * You can also use this component for a link.
 * Then you need to use the `as`-attribute.
 * @Example
 * <Button.Primary as="a"></Button.Primary>
 */

/**
 * @deprecated Deprecated since lexus revamp. Use the ButtonPrimary react component instead.
 */
export const Primary = styled(Button).attrs<ButtonType>((props) => ({
    className: [
        "btn-primary l-micro-text",
        props.disabled ? "btn-disabled" : "",
        props.light ? "white" : "",
        props.buttonSize ? `btn-${props.buttonSize}` : "",
    ].join(" "),
}))<ButtonType>`
    ${styleProps};
`;

export const ButtonPrimary = styled(ButtonWrapper).attrs<ButtonType>(() => ({
    className: "btn-primary",
}))<{ hasIcon?: boolean } & ButtonType>`
    ${styleProps};
    line-height: 2.4rem;

    ${(props) =>
        props.fullWidth &&
        css`
            justify-content: center;
            width: 100%;
        `};

    & > .btn-text {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    span,
    ${Ellipsis} {
        font-family: ${lexusTheme.fonts.bold};
        font-size: 1.4rem;
        font-weight: 700;
        line-height: 4.2rem;
        text-transform: uppercase;
        color: ${lexusTheme.colors.white};
    }

    ${({ hasIcon }) =>
        hasIcon &&
        css`
            ${Text.Button} {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: ${lexusTheme.space[1]}px;
            }
        `};
`;

export const ButtonSecondary = styled(ButtonWrapper).attrs<ButtonType>(() => ({
    className: "btn-secondary",
}))<ButtonType>`
    ${styleProps};
    ${(props) =>
        props.fullWidth &&
        css`
            justify-content: center;
            width: 100%;
        `};
`;

export const ButtonTertiary = styled(ButtonWrapper).attrs<ButtonType>(() => ({
    className: "btn-tertiary",
}))<ButtonType>`
    ${styleProps};
    ${(props) =>
        props.fullWidth &&
        css`
            justify-content: center;
            width: 100%;
        `};
`;

export const ButtonWithIcon = styled(ButtonWrapper).attrs<ButtonType>(() => ({
    className: "btn-text-link",
}))<ButtonType>`
    ${styleProps};
    ${(props) =>
        props.fullWidth &&
        css`
            justify-content: center;
            width: 100%;
        `};
    ${Text.Button} {
        max-width: 100%;
        /* Fix RTL issue from Zaidan styles. (OR-7172) */
        &.icon-left {
            .l-icon::before {
                ${({ theme }) =>
                    theme.isRTL &&
                    css`
                        transform: scaleX(1) !important;
                    `};
            }
        }
        /* Fix strange hover effect from parent CSS */
        &:hover {
            &::after {
                display: none !important;
            }
        }
    }
    ${Text.IconButton} {
        text-overflow: ellipsis;
        overflow-x: hidden;
    }
`;

/* BUTTON ICON STYLES */

const linkHasIconStyles = css`
    display: inline-flex;
    align-items: center;
    ${IconWrapper} {
        margin-inline-end: ${lexusTheme.space[1]}px;
    }
`;

const linkHasIconRightStyles = css`
    display: inline-flex;
    align-items: center;
    ${IconWrapper} {
        margin-inline-start: ${lexusTheme.space[1]}px;
        margin-inline-end: 0;
    }
`;

type LinkType = {
    hasIcon?: boolean;
    hasIconRight?: boolean;
    small?: boolean;
    color?: ColorType;
    noUppercase?: boolean;
    fullWidth?: boolean;
} & ButtonType;

/**
 * Button.Link defaults to small buttons. Add the `large` prop for having a larger button.
 */

export const Link = styled.button<LinkType>`
    display: inline-block;
    margin: 0;
    font-family: ${lexusTheme.fonts.book};
    font-size: 1.4rem;
    letter-spacing: 0.1rem;
    line-height: 1.9rem;
    text-transform: uppercase;
    color: ${(props) => (props.color ? lexusTheme.colors[props.color] : lexusTheme.colors.dark)};
    border: none;
    ${(props) => props.hasIcon && linkHasIconStyles};
    ${(props) => props.hasIconRight && linkHasIconRightStyles};
    ${styleProps};
    ${(props) =>
        props.large &&
        css`
            font-size: 1.8rem;
            line-height: 2.4rem;
        `};
    ${(props) =>
        props.small &&
        css`
            font-size: 1.4rem;
            line-height: 1.8rem;
        `};
    ${(props) =>
        props.noUppercase &&
        css`
            text-transform: none;
        `};
    ${(props) =>
        props.fullWidth &&
        css`
            display: flex;
            justify-content: space-between;
            width: 100%;
        `};
`;
